import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useWhatsAppUnread } from "../contexts/WhatsAppUnreadContext";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";
import NewAppointmentModal from "../components/NewAppointmentModal";
import { toast } from "sonner";
import { Sparkles, Send, Phone, Bot, Filter, CalendarPlus, RefreshCw, Pause, Play, Clock, WifiOff, Volume2, VolumeX } from "lucide-react";

const STATUS_FILTERS = ["Todos", "Nuevo", "Agendado", "Seguimiento", "Urgente", "Perdido"];
const LIST_POLL_MS = 8000; // PUENTE-8 PASO 4: lista cada 8s
const THREAD_POLL_MS = 15000; // PASO 4: el hilo abierto se queda en 15s
const FAILURE_THRESHOLD = 3; // marca visualmente que la conexión se perdió tras varios fallos seguidos
const SOUND_MUTE_KEY = "surit_inbox_sound_muted";
const SCROLL_BOTTOM_THRESHOLD = 60; // px de tolerancia para considerar "ya estaba abajo del todo"

function formatElapsed(iso) {
  if (!iso) return "nunca";
  const diffMin = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (diffMin < 60) return `${diffMin} min`;
  return `${Math.floor(diffMin / 60)}h ${diffMin % 60}min`;
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

// PUENTE-8 PASO 3: "ding-dong" ascendente -- dos tonos cortos, el segundo más agudo que el
// primero, más fuerte que el tono único de PUENTE-6/7 (0.15 -> 0.45 de ganancia) para que se
// oiga sobre ruido de mostrador sin sonar a alarma. Web Audio, sin archivo de audio en el repo.
// Nunca debe romper el Inbox si el navegador no soporta AudioContext. Se dispara UNA vez por
// ciclo de poll sin importar cuántas conversaciones traigan mensaje nuevo -- el call site
// (pollList) ya vive fuera del loop por conversación, sin cambios en esa parte.
function playNotificationSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const playTone = (freq, startTime, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.45, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startTime);
      osc.stop(startTime + duration);
    };
    const now = ctx.currentTime;
    playTone(700, now, 0.12); // primer tono, más grave
    playTone(1000, now + 0.16, 0.14); // segundo tono, más agudo -- 40ms de gap tras el primero
  } catch {
    // silencioso a propósito
  }
}

// Misma lógica en demo y en real (PUENTE-6/7 PASO 4: "no bifurques el comportamiento de la UI").
// No depende de no_leidos (que no existe en demo) -- solo compara si "lo último que cambió" es
// distinto de lo que había en el poll anterior.
function freshnessKey(c, isDemo) {
  if (isDemo) return c.last_activity || c.last_message || null;
  return c.direccion_ultimo === "inbound" ? c.ultimo_mensaje_at : null;
}

// PUENTE-8 PASO 1: solo para MOSTRAR un nombre -- nunca se usa para permisos, agendar, ni
// vincular registros. Si algún día se necesita el vínculo real paciente<->conversación como
// dato, eso se construye en el backend, no aquí.
//
// Comparamos los ÚLTIMOS 10 DÍGITOS en vez de reimplementar normalize_phone() en JS -- un
// número mexicano siempre tiene los mismos 10 dígitos significativos sin importar si el origen
// es wa_id (52+1+10 dígitos, formato que manda Meta) o E.164 (+52+10 dígitos, formato de
// patients.phone). SUPUESTO EXPLÍCITO, documentado porque puede dejar de ser cierto: esto
// asume pacientes mexicanos. Si algún día hay pacientes con número extranjero, los últimos 10
// dígitos de dos países distintos pueden coincidir por azar y cruzar con el paciente equivocado
// -- pregunta abierta para cuando existan clínicas con pacientes internacionales (Jaret).
function last10Digits(raw) {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  return digits.length >= 10 ? digits.slice(-10) : null;
}

function formatPhoneDisplay(telefono) {
  const last10 = last10Digits(telefono);
  if (!last10) return telefono || "";
  return `+52 ${last10.slice(0, 3)} ${last10.slice(3, 6)} ${last10.slice(6)}`;
}

export default function Inbox() {
  const { user } = useAuth();
  // PUENTE-4/5: bifurcación explícita — sesión demo (claim del JWT, no clinics.is_demo) sigue
  // leyendo la tabla local vía los endpoints viejos; cualquier otra sesión usa el proxy real a
  // agency. Ver "Bifurcación demo" en CLAUDE.md.
  const isDemo = !!user?.is_demo_session;
  const { reportConversations, setExternallyFed } = useWhatsAppUnread();

  // PUENTE-8 PASO 6: mientras Inbox está montado, el badge del menú se alimenta de la lista
  // que este componente YA trae cada 8s -- no dispara su propia petición en paralelo. Al
  // desmontarse, el contexto vuelve a pedir por su cuenta (máx. 15s de retraso, su propio tick).
  useEffect(() => {
    if (isDemo) return;
    setExternallyFed(true);
    return () => setExternallyFed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // demo: fila de conversations + .messages. Real: resumen de agency ({telefono,...}).
  const [threadMessages, setThreadMessages] = useState([]); // solo modo real
  const [filter, setFilter] = useState("Todos");
  const [sending, setSending] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [draft, setDraft] = useState("");
  const [loadError, setLoadError] = useState(null);
  const [params, setParams] = useSearchParams();
  const [apptOpen, setApptOpen] = useState(false);

  // PUENTE-6: polling
  const [connectionLost, setConnectionLost] = useState(false);
  const [newKeys, setNewKeys] = useState(() => new Set()); // marca visual de "mensaje nuevo" en la lista (demo) / disparo de sonido (ambos modos)
  const [soundMuted, setSoundMuted] = useState(() => localStorage.getItem(SOUND_MUTE_KEY) === "true");
  // PUENTE-8 PASO 1: teléfono (últimos 10 dígitos) -> nombre de paciente. Solo modo real -- demo
  // ya trae patient_name real por conversación, no necesita este cruce.
  const [patientPhoneMap, setPatientPhoneMap] = useState(() => new Map());

  const activeRef = useRef(null);
  const failuresRef = useRef(0);
  const firstPollRef = useRef(true);
  const prevFreshnessRef = useRef({});
  const messagesRef = useRef(null);
  const wasAtBottomRef = useRef(true);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // PUENTE-8 PASO 1 / PRECISIÓN 2: se pide UNA sola vez al montar -- no entra al ciclo de
  // polling (los pacientes de una clínica no cambian cada 8s). Si falla, no bloquea nada: el
  // mapa se queda vacío y la resolución de nombre cae a display_name + teléfono, igual que hoy.
  useEffect(() => {
    if (isDemo) return;
    api
      .get("/patients")
      .then(({ data }) => {
        const map = new Map();
        for (const p of data.items || []) {
          const key = last10Digits(p.phone);
          if (key && p.name) map.set(key, p.name);
        }
        setPatientPhoneMap(map);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo]);

  const resolveRealName = (c) => {
    const key = last10Digits(c.telefono);
    const patientName = key ? patientPhoneMap.get(key) : null;
    return patientName || c.display_name || c.telefono;
  };

  const openConvReal = async (telefono, summary) => {
    wasAtBottomRef.current = true;
    try {
      const { data } = await api.get("/whatsapp/conversations/thread", { params: { telefono } });
      setThreadMessages(data.items || []);
      setActive(summary || { telefono });
      setNewKeys((prev) => {
        if (!prev.has(telefono)) return prev;
        const n = new Set(prev);
        n.delete(telefono);
        return n;
      });
      // Abrir marca como leído en agency (PUENTE-7). Best-effort: si falla, no bloquea la
      // apertura del hilo -- el número real se queda desactualizado hasta el próximo poll de
      // lista, pero el usuario ya puede leer/responder. El badge del menú (PASO 6) se actualiza
      // aquí mismo, de inmediato, con el dato local que ya tenemos -- no espera la respuesta
      // de mark-read ni dispara una petición nueva.
      setItems((prev) => {
        const next = prev.map((c) => (c.telefono === telefono ? { ...c, no_leidos: 0 } : c));
        reportConversations(next);
        return next;
      });
      api.post("/whatsapp/conversations/mark-read", { telefono }).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el historial de esta conversación.");
    }
  };

  const load = async (selectKey) => {
    setLoadError(null);
    try {
      if (isDemo) {
        const { data } = await api.get("/conversations");
        setItems(data.items || []);
        const id = selectKey || params.get("conv") || (data.items || [])[0]?.id;
        if (id) {
          const r = await api.get(`/conversations/${id}`);
          setActive(r.data);
        }
      } else {
        const { data } = await api.get("/whatsapp/conversations");
        const list = data.items || [];
        setItems(list);
        reportConversations(list);
        const tel = selectKey || params.get("conv") || list[0]?.telefono;
        if (tel) {
          await openConvReal(tel, list.find((c) => c.telefono === tel));
        }
      }
    } catch (e) {
      // PASO 1: si agency no responde o falla, error explícito -- nunca una bandeja vacía que
      // se vea igual que "sin conversaciones".
      setLoadError(
        isDemo
          ? "No se pudieron cargar las conversaciones"
          : e?.response?.data?.detail || "No se pudo conectar con WhatsApp. Intenta de nuevo."
      );
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  // PUENTE-8 PASO 4: lista (8s) e hilo (15s) son dos ciclos independientes -- antes eran uno
  // solo a 15s. NO usa loadError (eso borraría toda la pantalla) -- solo cuenta fallos
  // consecutivos y muestra un aviso no intrusivo tras varios seguidos, sin tocar lo que ya está
  // en pantalla.
  const pollList = useCallback(async () => {
    try {
      let list;
      if (isDemo) {
        const { data } = await api.get("/conversations");
        list = data.items || [];
      } else {
        const { data } = await api.get("/whatsapp/conversations");
        list = data.items || [];
      }
      failuresRef.current = 0;
      setConnectionLost(false);
      setItems(list);
      if (!isDemo) reportConversations(list);

      if (!firstPollRef.current) {
        const prevMap = prevFreshnessRef.current;
        const arrived = [];
        for (const c of list) {
          const key = isDemo ? c.id : c.telefono;
          const fresh = freshnessKey(c, isDemo);
          if (fresh && fresh !== prevMap[key]) arrived.push(key);
        }
        if (arrived.length > 0) {
          setNewKeys((prev) => new Set([...prev, ...arrived]));
          if (!soundMuted) playNotificationSound();
        }
      }
      const nextMap = {};
      for (const c of list) {
        const key = isDemo ? c.id : c.telefono;
        const fresh = freshnessKey(c, isDemo);
        if (fresh) nextMap[key] = fresh;
      }
      prevFreshnessRef.current = nextMap;
      firstPollRef.current = false;
    } catch {
      failuresRef.current += 1;
      if (failuresRef.current >= FAILURE_THRESHOLD) setConnectionLost(true);
    }
  }, [isDemo, soundMuted, reportConversations]);

  const pollThread = useCallback(async () => {
    const curActive = activeRef.current;
    if (!curActive) return;
    try {
      const el = messagesRef.current;
      const atBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD : true;
      wasAtBottomRef.current = atBottom;
      if (isDemo) {
        const { data } = await api.get(`/conversations/${curActive.id}`);
        setActive(data);
      } else {
        const { data } = await api.get("/whatsapp/conversations/thread", { params: { telefono: curActive.telefono } });
        setThreadMessages(data.items || []);
      }
    } catch {
      // no romper el hilo visible por un fallo de polling puntual
    }
  }, [isDemo]);

  // Pausa en pestaña oculta, reanuda y refresca de inmediato al volver -- los dos ciclos juntos,
  // para no duplicar el cableado de visibilitychange.
  useEffect(() => {
    let listInterval = null;
    let threadInterval = null;
    const start = () => {
      if (!listInterval) listInterval = setInterval(pollList, LIST_POLL_MS);
      if (!threadInterval) threadInterval = setInterval(pollThread, THREAD_POLL_MS);
    };
    const stop = () => {
      if (listInterval) {
        clearInterval(listInterval);
        listInterval = null;
      }
      if (threadInterval) {
        clearInterval(threadInterval);
        threadInterval = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        pollList();
        pollThread();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [pollList, pollThread]);

  // PASO 2: solo baja al fondo si ya estaba abajo del todo antes de que llegaran datos nuevos
  // (calculado en pollThread/openConv*/sendMessage, antes de actualizar el estado).
  useEffect(() => {
    const el = messagesRef.current;
    if (!el || !wasAtBottomRef.current) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo ? active?.messages : threadMessages]);

  // PUENTE-8 PASO 2: no leídos primero -- sort estable, real mode únicamente (demo no tiene
  // no_leidos, no se inventa un orden de "no leído" que no existe).
  const filtered = useMemo(() => {
    let list = isDemo && filter !== "Todos" ? items.filter((i) => i.status === filter) : items;
    if (!isDemo) {
      list = [...list].sort((a, b) => (b.no_leidos > 0 ? 1 : 0) - (a.no_leidos > 0 ? 1 : 0));
    }
    return list;
  }, [items, filter, isDemo]);

  const openConv = async (c) => {
    if (isDemo) {
      wasAtBottomRef.current = true;
      setParams({ conv: c.id });
      const r = await api.get(`/conversations/${c.id}`);
      setActive(r.data);
      setNewKeys((prev) => {
        if (!prev.has(c.id)) return prev;
        const n = new Set(prev);
        n.delete(c.id);
        return n;
      });
    } else {
      setParams({ conv: c.telefono });
      await openConvReal(c.telefono, c);
    }
  };

  const sendMessage = async (body) => {
    if (!body.trim() || !active) return;
    setSending(true);
    wasAtBottomRef.current = true;
    try {
      if (isDemo) {
        const { data } = await api.post(`/conversations/${active.id}/messages`, { body, sender: "agent" });
        setActive(data);
        setItems((prev) => prev.map((c) => (c.id === data.id ? { ...c, last_message: data.last_message, last_activity: data.last_activity } : c)));
      } else {
        await api.post("/whatsapp/conversations/send", { telefono: active.telefono, texto: body });
        await load(active.telefono);
      }
      setDraft("");
    } catch (e) {
      if (!isDemo && e?.response?.status === 409 && e?.response?.data?.code === "WINDOW_CLOSED") {
        toast.error(e.response.data.detail || "La ventana de 24 horas está cerrada.");
        setActive((prev) => (prev ? { ...prev, ventana_24h_abierta: false, ultimo_inbound_at: e.response.data.ultimo_inbound_at } : prev));
      } else {
        toast.error(e?.response?.data?.detail || "No se pudo enviar el mensaje");
      }
    } finally {
      setSending(false);
    }
  };

  const sendAISuggestion = async () => {
    if (active?.ai_suggestion) {
      await sendMessage(active.ai_suggestion);
      toast.success("Respuesta IA enviada");
    }
  };

  const regenerateAI = async () => {
    if (!active) return;
    setRegenerating(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/ai-suggest`);
      setActive((prev) => ({ ...prev, ai_suggestion: data.ai_suggestion || data.suggestion || "" }));
      toast.success("Sugerencia IA regenerada con Claude Haiku");
    } catch {
      toast.error("No se pudo regenerar la sugerencia");
    } finally {
      setRegenerating(false);
    }
  };

  // PRECISIÓN 3: en demo, "Tomar control" sigue siendo el handoff viejo (cosmético sobre datos
  // de ejemplo, ver deuda en CLAUDE.md). En modo real llama a /pause, que sí silencia a Sofi.
  const handoff = async () => {
    if (!active) return;
    if (isDemo) {
      await api.post(`/conversations/${active.id}/handoff`);
      setActive({ ...active, handed_off: true, ai_active: false });
      toast.success("Conversación pasada a humano");
      return;
    }
    setPausing(true);
    try {
      const { data } = await api.post("/whatsapp/conversations/pause", { telefono: active.telefono });
      setActive((prev) => ({ ...prev, ai_paused: true, paused_until: data.paused_until }));
      toast.success("IA pausada — tú tomas la conversación");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo pausar la IA");
    } finally {
      setPausing(false);
    }
  };

  const resumeAI = async () => {
    if (!active || isDemo) return;
    setPausing(true);
    try {
      await api.post("/whatsapp/conversations/resume", { telefono: active.telefono });
      setActive((prev) => ({ ...prev, ai_paused: false, paused_until: null }));
      toast.success("IA reactivada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reactivar la IA");
    } finally {
      setPausing(false);
    }
  };

  const toggleMute = () => {
    setSoundMuted((prev) => {
      const next = !prev;
      localStorage.setItem(SOUND_MUTE_KEY, String(next));
      return next;
    });
  };

  if (loadError) return <div className="p-8 text-rose-500">{loadError}</div>;

  const messages = isDemo ? active?.messages || [] : threadMessages;
  const windowClosed = !isDemo && active && active.ventana_24h_abierta === false;
  const composerDisabled = sending || windowClosed;

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-7xl mx-auto w-full">
      <PageHeader
        title="Bandeja WhatsApp"
        subtitle={isDemo ? "Tus conversaciones en tiempo real, atendidas por Sofi (IA)." : "Conversaciones reales de WhatsApp, atendidas por Sofi (IA)."}
        actions={
          <div className="flex items-center gap-2 text-sm">
            {isDemo && (
              <>
                <Filter className="w-4 h-4 text-slate-400" />
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-sm rounded-lg border border-slate-300 bg-white px-2 py-1.5" data-testid="inbox-filter">
                  {STATUS_FILTERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </>
            )}
            <button
              onClick={toggleMute}
              className="p-2 rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50"
              title={soundMuted ? "Activar sonido de notificaciones" : "Silenciar sonido de notificaciones"}
              data-testid="inbox-mute-toggle"
            >
              {soundMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-200px)]">
        {/* List pane */}
        <div className="lg:col-span-4 bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {filtered.length} conversación{filtered.length === 1 ? "" : "es"}
          </div>
          {connectionLost && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-xs font-medium flex items-center gap-1.5" data-testid="inbox-connection-lost">
              <WifiOff className="w-3.5 h-3.5" /> Conexión perdida — reintentando…
            </div>
          )}
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">No hay conversaciones en este estado.</div>
          ) : (
            <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {filtered.map((c) => {
                const key = isDemo ? c.id : c.telefono;
                // PUENTE-8 PASO 1: orden de resolución -- nombre del paciente (cruce por
                // teléfono) > display_name de Meta > teléfono solo. El teléfono formateado se
                // muestra SIEMPRE debajo, aparte del nombre resuelto (nunca lo sustituye).
                const name = isDemo ? c.patient_name : resolveRealName(c);
                const preview = isDemo ? c.last_message : c.ultimo_mensaje;
                const isActive = isDemo ? active?.id === c.id : active?.telefono === c.telefono;
                // PASO 2: en real, "no leído" es no_leidos real de agency (baja de verdad al
                // marcar leído) -- en demo sigue siendo el marcador local de PUENTE-6/7, no hay
                // no_leidos que inventar ahí.
                const unreadCount = !isDemo ? c.no_leidos || 0 : 0;
                const isUnread = !isDemo ? unreadCount > 0 : newKeys.has(key);
                const rowBg = isActive ? "bg-blue-50/60" : isUnread ? "bg-indigo-50/70" : "";
                return (
                  <li key={key}>
                    <button
                      onClick={() => openConv(c)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${rowBg}`}
                      data-testid={`inbox-item-${key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-semibold">
                            {(name || "?")[0]}
                          </div>
                          {isDemo && isUnread && (
                            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-brand-electric border-2 border-white" data-testid={`inbox-new-${key}`} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-slate-900 truncate ${isUnread ? "font-bold" : "font-medium"}`}>{name}</span>
                            {isDemo ? (
                              <StatusPill status={c.status} />
                            ) : (
                              c.ai_paused && (
                                <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                  IA pausada
                                </span>
                              )
                            )}
                          </div>
                          {!isDemo && (
                            <div className="text-xs text-slate-400 truncate">{formatPhoneDisplay(c.telefono)}</div>
                          )}
                          <div className={`text-sm truncate ${isUnread ? "text-slate-800" : "text-slate-500"}`}>{preview}</div>
                        </div>
                        {!isDemo && unreadCount > 0 && (
                          <span
                            className="shrink-0 self-start mt-0.5 text-[11px] font-bold bg-brand-electric text-white rounded-full px-1.5 py-0.5 min-w-[20px] text-center"
                            data-testid={`inbox-unread-count-${key}`}
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Chat pane */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden flex flex-col">
          {!active ? (
            <div className="m-auto text-center text-slate-500 p-10">Selecciona una conversación para empezar.</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-semibold">
                  {(isDemo ? active.patient_name : resolveRealName(active))[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {isDemo ? active.patient_name : resolveRealName(active)}
                    {isDemo && <StatusPill status={active.status} />}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Phone className="w-3 h-3" />
                    {isDemo ? active.patient_phone : formatPhoneDisplay(active.telefono)}
                    {!isDemo && (
                      <span className={`inline-flex items-center gap-1 ${active.ventana_24h_abierta ? "text-emerald-600" : "text-slate-400"}`}>
                        <Clock className="w-3 h-3" />
                        {active.ventana_24h_abierta ? "Ventana de 24h abierta" : `Ventana cerrada · último mensaje hace ${formatElapsed(active.ultimo_inbound_at)}`}
                      </span>
                    )}
                  </div>
                </div>

                {isDemo ? (
                  <>
                    {!active.handed_off && (
                      <button onClick={handoff} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50" data-testid="inbox-handoff">
                        Pasar a humano
                      </button>
                    )}
                    {active.handed_off && (
                      <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">Asignado a humano</span>
                    )}
                  </>
                ) : active.ai_paused ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                      IA pausada{active.paused_until ? ` hasta las ${formatTime(active.paused_until)}` : ""}
                    </span>
                    <button
                      onClick={resumeAI}
                      disabled={pausing}
                      className="text-xs font-medium px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 inline-flex items-center gap-1"
                      data-testid="inbox-resume"
                    >
                      <Play className="w-3.5 h-3.5" /> Reactivar IA
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handoff}
                    disabled={pausing}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-60 inline-flex items-center gap-1"
                    data-testid="inbox-handoff"
                  >
                    <Pause className="w-3.5 h-3.5" /> Tomar control
                  </button>
                )}
              </div>

              {/* Messages */}
              <div ref={messagesRef} className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,#F8FAFC,#FFFFFF)] p-5 space-y-2 flex flex-col">
                {messages.map((m, i) => {
                  const fromPatient = isDemo ? m.sender === "patient" : m.direccion === "inbound";
                  const isAi = isDemo && m.sender === "ai";
                  const body = isDemo ? m.body : m.display_body;
                  const ts = isDemo ? m.ts : m.created_at;
                  return (
                    <div key={isDemo ? m.id : m.wamid || i} className={`flex ${fromPatient ? "justify-start" : "justify-end"}`}>
                      <div className={`bubble ${fromPatient ? "bubble-patient" : isAi ? "bubble-ai" : "bubble-agent"}`}>
                        {isAi && (
                          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-0.5 flex items-center gap-1">
                            <Bot className="w-3 h-3" /> Sofi IA
                          </div>
                        )}
                        {!isDemo && m.message_type === "audio" ? (
                          <>
                            <div className="text-[10px] font-bold mb-0.5">Nota de voz</div>
                            {m.transcrita && (
                              <>
                                <div>{body}</div>
                                <div className={`text-[10px] mt-1 ${fromPatient ? "text-slate-400" : "opacity-70"}`}>
                                  Transcripción automática; puede tener errores.
                                </div>
                              </>
                            )}
                          </>
                        ) : <div>{body}</div>}
                        <div className={`text-[10px] mt-1 ${fromPatient ? "text-slate-400" : "opacity-70"}`}>
                          {ts && new Date(ts).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* AI suggestion (solo demo — no hay señal real de esto en modo real) */}
              {isDemo && !active.handed_off && (
                <div className="px-5 pt-3">
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 text-sm text-indigo-900 flex items-start gap-3" data-testid="ai-suggestion-box">
                    <Sparkles className="w-4 h-4 mt-0.5 text-indigo-600" />
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-600 flex items-center gap-2">
                        IA sugiere responder
                      </div>
                      <div className="mt-1 leading-snug min-h-[1.2rem]">{active.ai_suggestion || <span className="italic text-indigo-400">Sin sugerencia. Pulsa "Regenerar" para generar una con IA.</span>}</div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button onClick={regenerateAI} disabled={regenerating} className="text-xs font-medium border border-indigo-300 text-indigo-700 px-3 py-1.5 rounded-lg hover:bg-indigo-100 disabled:opacity-60 inline-flex items-center gap-1" data-testid="ai-regenerate">
                        <RefreshCw className={`w-3.5 h-3.5 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "…" : "Regenerar"}
                      </button>
                      <button onClick={sendAISuggestion} disabled={sending || !active.ai_suggestion} className="text-xs font-medium bg-brand-electric text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-60" data-testid="ai-send-suggestion">
                        Enviar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="p-4 border-t border-slate-200 flex items-center gap-2">
                {windowClosed ? (
                  <div className="flex-1 rounded-full bg-slate-100 px-4 py-2.5 text-sm text-slate-500" data-testid="inbox-window-closed">
                    Ventana de 24h cerrada — último mensaje del paciente hace {formatElapsed(active.ultimo_inbound_at)}. No se puede escribir hasta que el paciente vuelva a escribir.
                  </div>
                ) : (
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !composerDisabled && sendMessage(draft)}
                    placeholder="Escribe un mensaje…"
                    disabled={composerDisabled}
                    className="flex-1 rounded-full bg-slate-100 px-4 py-2.5 text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                    data-testid="inbox-composer"
                  />
                )}
                {isDemo && (
                  <button
                    onClick={() => setApptOpen(true)}
                    className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium bg-white border border-slate-300 px-3 py-2 rounded-lg hover:bg-slate-50"
                    data-testid="inbox-schedule"
                  >
                    <CalendarPlus className="w-4 h-4" /> Agendar
                  </button>
                )}
                <button
                  onClick={() => sendMessage(draft)}
                  disabled={composerDisabled || !draft.trim()}
                  className="inline-flex items-center gap-1.5 text-sm font-medium bg-brand-electric text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
                  data-testid="inbox-send"
                >
                  <Send className="w-4 h-4" /> Enviar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isDemo && (
        <NewAppointmentModal
          open={apptOpen}
          onClose={() => setApptOpen(false)}
          defaultPatient={active ? { id: active.patient_id, name: active.patient_name, phone: active.patient_phone } : null}
          onCreated={async () => {
            toast.success("Cita creada — paciente pasó a 'Agendado'");
            await api.post(`/conversations/${active.id}/messages`, {
              body: "✅ Plantilla 'Confirmación de cita' enviada",
              sender: "ai",
            });
            load(active.id);
          }}
        />
      )}
    </div>
  );
}
